import React from "react";
import RetreatListing from "../components/RetreatListing";
import Seo from "../components/Seo";
import { HERO_IMAGE_URL } from "../lib/heroImage";
import { GraduationCap } from "lucide-react";

const LOCKED_TYPE = "Yoga Teacher Training";

const faqItems = [
  {
    question: "What is a Yoga Teacher Training (YTT) course?",
    answer:
      "A Yoga Teacher Training is a structured certification program (commonly 100, 200 or 300-hour) covering asana, anatomy, philosophy and teaching methodology. On completion you can register as a teacher and start teaching yoga.",
  },
  {
    question: "Where are the best teacher training courses in India?",
    answer:
      "Rishikesh is the most popular destination for traditional YTT, while Goa, Kerala and Dharamsala also offer excellent Yoga Alliance certified programs. Use the filters to compare locations, dates and prices.",
  },
  {
    question: "How long does a 200-hour YTT take?",
    answer:
      "Most residential 200-hour teacher training courses run for around 3-4 weeks. Compare each program's batches and dates to find one that fits your schedule.",
  },
];

function TeacherTraining() {
  const title = "Yoga Teacher Training in India";
  const description =
    "Compare Yoga Teacher Training courses (100, 200 & 300-hour YTT) across India - Rishikesh, Goa, Kerala and more. Compare batches, dates, certification, ratings and prices.";
  const canonicalPath = "/yoga-teacher-training";

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        name: title,
        url: `https://yogaretreatsbooking.com${canonicalPath}`,
        description,
      },
      {
        "@type": "FAQPage",
        mainEntity: faqItems.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: f.answer,
          },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-gray-50/50 pt-16">
      <Seo
        title={title}
        description={description}
        canonicalPath={canonicalPath}
        ogImagePath="/favicon.ico"
        jsonLd={jsonLd}
      />

      {/* Hero Section */}
      <div className="relative h-[340px] bg-gray-900 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE_URL}
            alt="Yoga teacher training in India"
            className="w-full h-full object-cover opacity-60"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-900/50 to-gray-900" />
        </div>

        <div className="relative container mx-auto px-4 h-full flex flex-col justify-center items-center text-center">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 text-xs font-bold uppercase tracking-widest mb-4 border border-blue-500/30 backdrop-blur-md">
              <GraduationCap size={14} /> Certified YTT in India
            </span>
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4 leading-tight">
              Yoga Teacher Training in India
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Compare 100, 200 and 300-hour Yoga Teacher Training courses across India - from Rishikesh and Goa to
              Kerala and the Himalayas. Find batches, certification, reviews and prices in one place.
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 -mt-10 relative z-10 pb-20">
        <RetreatListing
          lockedType={LOCKED_TYPE}
          heading="Yoga Teacher Training Courses in India"
          subheading="Compare YTT courses across India by destination, duration, rating and price"
        />

        <section className="mt-12 bg-white rounded-3xl p-8 shadow-sm border border-gray-100">
          <h2 className="text-2xl font-black text-gray-900 mb-4">FAQs about Yoga Teacher Training in India</h2>
          <div className="space-y-4">
            {faqItems.map((item) => (
              <details key={item.question} className="group rounded-2xl border border-gray-100 p-4">
                <summary className="cursor-pointer font-bold text-gray-900">{item.question}</summary>
                <p className="mt-2 text-gray-600 text-sm leading-relaxed">{item.answer}</p>
              </details>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default TeacherTraining;
